import {
  startOllama, installOllama, pullOllamaModel,
  OllamaStatus, OllamaModelsResponse,
} from '@/api/desktop';

// 本地 AI 一键安装链（09-05 拍板「真做成一键」）：装 Ollama（缺失时，Pro）→ 拉起服务
// → 常驻小模型 → 嵌入模型（两个基础模型=免费通道）→ 选定大模型（Pro，可选）。
// ModelManagerCard 与 ModelManagerBanner 共用同一条链（统一口径，改一处两处生效）。

export interface SetupProgress {
  message: string;
  percent: number | null;
}

/** 失败分类：pro-install=装 Ollama 被 Pro 门拦；pro-big=大模型被 Pro 门拦；error=真错误 */
export type SetupFailKind = 'pro-install' | 'pro-big' | 'error';

export class SetupFail extends Error {
  kind: SetupFailKind;
  constructor(kind: SetupFailKind, message: string) {
    super(message);
    this.kind = kind;
  }
}

export async function runLocalAISetup(opts: {
  status: OllamaStatus | null;
  catalog: OllamaModelsResponse;
  /** 选定大模型 id；不传 = 只装两个基础模型（免费通道，横幅一键走这里） */
  selectedBigId?: string;
  onProgress: (p: SetupProgress) => void;
}): Promise<void> {
  const { status, catalog, selectedBigId, onProgress } = opts;

  // ① Ollama 未安装 → 自动安装（Pro；免费用户被 403 拦下转手动指引）
  if (status && !status.binary) {
    onProgress({ message: '正在安装 Ollama…', percent: null });
    try {
      await installOllama((e) => {
        if (e.message) onProgress({ message: e.message, percent: null });
      });
    } catch (e: any) {
      throw e?.status === 403
        ? new SetupFail('pro-install', '一键自动安装 Ollama 为 Pro 会员功能')
        : e;
    }
  }

  // ② 确保服务在跑
  onProgress({ message: '正在启动 Ollama 服务…', percent: null });
  await startOllama();

  // ③ 依次拉取：常驻小模型 → 嵌入模型（免费通道）→ 选定大模型（Pro）
  const picked = selectedBigId ? catalog.models.find((m) => m.id === selectedBigId) : undefined;
  const queue: { model: string; label: string; pro?: boolean }[] = [];
  if (!status?.required?.chat) queue.push({ model: 'qwen3.5:0.8b', label: '常驻小模型' });
  if (!catalog.embed_installed) queue.push({ model: catalog.embed_model, label: '嵌入模型' });
  if (picked && !picked.installed) queue.push({ model: picked.ollama_model, label: picked.name, pro: true });

  for (let i = 0; i < queue.length; i++) {
    const item = queue[i];
    onProgress({ message: `正在下载 ${i + 1}/${queue.length}：${item.label}（${item.model}）`, percent: 0 });
    try {
      await pullOllamaModel(item.model, (e) => {
        if (e.total && e.completed) {
          onProgress({
            message: `正在下载 ${i + 1}/${queue.length}：${item.label}（${item.model}）`,
            percent: Math.min(99, Math.round((e.completed * 100) / e.total)),
          });
        }
        if (e.status === 'error') throw new Error(e.message || '下载失败');
      });
    } catch (e: any) {
      throw e?.status === 403 && item.pro
        ? new SetupFail('pro-big', '大模型一键下载为 Pro 会员功能')
        : e;
    }
    onProgress({ message: `${item.label} 下载完成`, percent: 100 });
  }
}
